import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Calendar as CalendarIcon, Clock, MapPin, Check, Minus, X as XIcon,
  Send, Loader2, Download, Trash2, Pencil, ExternalLink, Sparkles,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { buildGoogleCalendarUrl } from '@/lib/googleCalendar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getInitials = (name) =>
  (name || '?').split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();

const fmtTime = (hhmm) => {
  if (!hhmm || !/^\d{2}:\d{2}/.test(hhmm)) return hhmm || '';
  const [hh, mm] = hhmm.split(':').map(Number);
  const period = hh >= 12 ? 'PM' : 'AM';
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, '0')} ${period}`;
};

const RSVP_META = {
  going:   { label: 'Going',      color: 'bg-teal-600 hover:bg-teal-700 text-white', icon: Check,  pill: 'bg-teal-50 text-teal-700 border-teal-200' },
  maybe:   { label: 'Maybe',      color: 'bg-amber-500 hover:bg-amber-600 text-white', icon: Minus,  pill: 'bg-amber-50 text-amber-700 border-amber-200' },
  declined:{ label: "Can't make it", color: 'bg-stone-500 hover:bg-stone-600 text-white', icon: XIcon, pill: 'bg-stone-100 text-stone-700 border-stone-200' },
};

export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  const fetchEvent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events/${eventId}`, { withCredentials: true });
      setEvent(data);
    } catch (err) {
      toast.error('Event not found');
      navigate('/calendar');
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  const fetchComments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events/${eventId}/comments`, { withCredentials: true });
      setComments(data);
    } catch (_) {}
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
    fetchComments();
  }, [fetchEvent, fetchComments]);

  const setRsvp = async (status) => {
    setRsvpBusy(true);
    try {
      await axios.post(`${API}/events/${eventId}/rsvp`, { status }, { withCredentials: true });
      await fetchEvent();
    } catch (err) {
      toast.error('Failed to update RSVP');
    } finally {
      setRsvpBusy(false);
    }
  };

  const clearRsvp = async () => {
    setRsvpBusy(true);
    try {
      await axios.delete(`${API}/events/${eventId}/rsvp`, { withCredentials: true });
      await fetchEvent();
    } catch (_) {} finally {
      setRsvpBusy(false);
    }
  };

  const handleDownloadIcs = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/ics`, {
        withCredentials: true, responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const safeTitle = (event?.title || 'event').replace(/[^a-z0-9]/gi, '_');

      // iOS Safari / Capacitor WKWebView often ignores anchor.click() for blob
      // downloads — open the .ics content directly so the OS hands it to
      // Calendar. Read as text and use a data: URL fallback that works
      // reliably across web + native.
      const text = await blob.text();
      const dataUrl = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(text);

      // Prefer a real anchor click on web (triggers real download).
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${safeTitle}.ics`;
      a.rel = 'noopener';
      // Some iOS WKWebViews respect target=_blank + system opener.
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast.success('Downloaded! Open it to add to your calendar.');
    } catch (_) {
      toast.error('Download failed');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success('Event deleted');
      navigate('/calendar');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      const { data } = await axios.post(
        `${API}/events/${eventId}/comments`,
        { content: newComment.trim() },
        { withCredentials: true }
      );
      setComments((c) => [...c, data]);
      setNewComment('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not post');
    } finally {
      setPostingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      </div>
    );
  }
  if (!event) return null;

  const startDate = parseISO(event.start_date);
  const startTime = event.start_date?.match(/T(\d{2}:\d{2})/)?.[1];
  const endTime = event.end_date?.match(/T(\d{2}:\d{2})/)?.[1];
  const isHost = user?.id === event.host_id;
  const isAdmin = user?.role === 'admin';
  const canEdit = isHost || isAdmin || user?.id === event.created_by;
  const mapsUrl = event.location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`
    : null;

  const coverUrl = event.cover_url ? `${API}/files/${event.cover_url}` : null;
  const groupedRsvps = {
    going: event.rsvps?.filter((r) => r.status === 'going') || [],
    maybe: event.rsvps?.filter((r) => r.status === 'maybe') || [],
    declined: event.rsvps?.filter((r) => r.status === 'declined') || [],
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="event-detail-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/calendar')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
          data-testid="back-to-calendar-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to calendar
        </button>

        {/* Hero */}
        <div className="rounded-3xl overflow-hidden border border-stone-200 bg-white shadow-sm">
          <div
            className="relative h-40 md:h-56 bg-gradient-to-br from-teal-500 via-teal-600 to-teal-700"
            style={coverUrl ? { backgroundImage: `url(${coverUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
            <div className="absolute bottom-4 left-5 right-5 text-white">
              <div className="inline-flex items-center gap-1 text-xs font-medium bg-white/20 backdrop-blur rounded-full px-3 py-1 mb-2">
                <Sparkles className="w-3 h-3" /> Hosted by {event.host_name || 'a family member'}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="event-title">{event.title}</h1>
            </div>
          </div>

          <div className="p-5 md:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-stone-700">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-teal-600" />
                {format(startDate, 'EEEE, MMMM d, yyyy')}
              </div>
              {!event.all_day && startTime && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-teal-600" />
                  {fmtTime(startTime)}{endTime ? ` – ${fmtTime(endTime)}` : ''}
                </div>
              )}
              {event.all_day && (
                <span className="text-teal-600 font-medium">All day</span>
              )}
            </div>

            {event.location && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 font-medium bg-teal-50/60 rounded-full px-3 py-1.5"
                data-testid="event-location"
              >
                <MapPin className="w-4 h-4" /> {event.location}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}

            {event.description && (
              <p className="text-stone-700 whitespace-pre-wrap leading-relaxed" data-testid="event-description">
                {event.description}
              </p>
            )}

            {/* RSVP buttons */}
            <div className="border-t border-stone-100 pt-4">
              <p className="text-sm text-stone-500 mb-3">Will you make it?</p>
              <div className="flex flex-wrap gap-2" data-testid="rsvp-buttons">
                {Object.entries(RSVP_META).map(([status, meta]) => {
                  const Icon = meta.icon;
                  const active = event.my_rsvp === status;
                  return (
                    <Button
                      key={status}
                      onClick={() => (active ? clearRsvp() : setRsvp(status))}
                      disabled={rsvpBusy}
                      className={`rounded-full flex-1 sm:flex-none ${active ? meta.color : 'bg-white border border-stone-200 text-stone-700 hover:bg-stone-50'}`}
                      data-testid={`rsvp-${status}-btn`}
                    >
                      <Icon className="w-4 h-4 mr-1.5" /> {meta.label}
                      {active && <span className="ml-2 text-xs opacity-80">(you)</span>}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Counts + actions */}
            <div className="border-t border-stone-100 pt-4 flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <span className="font-semibold text-teal-700">{event.rsvp_counts?.going || 0}</span> going
                <span className="mx-1 text-stone-300">·</span>
                <span className="font-semibold text-amber-700">{event.rsvp_counts?.maybe || 0}</span> maybe
                <span className="mx-1 text-stone-300">·</span>
                <span className="font-semibold text-stone-700">{event.rsvp_counts?.declined || 0}</span> declined
              </div>
              <div className="ml-auto flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full min-h-[44px]"
                      data-testid="add-to-calendar-btn"
                    >
                      <Download className="w-4 h-4 mr-1.5" /> Add to calendar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                    <DropdownMenuItem
                      onClick={handleDownloadIcs}
                      className="cursor-pointer"
                      data-testid="add-to-apple-calendar"
                    >
                      <Download className="w-4 h-4 mr-2" /> Apple / Outlook (.ics)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => window.open(buildGoogleCalendarUrl(event), '_blank', 'noopener')}
                      className="cursor-pointer"
                      data-testid="add-to-google-calendar"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" /> Google Calendar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {canEdit && (
                  <>
                    <Button variant="outline" size="sm" asChild className="rounded-full">
                      <Link to="/calendar" data-testid="edit-event-link">
                        <Pencil className="w-4 h-4 mr-1.5" /> Edit
                      </Link>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-event-btn">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                          <AlertDialogDescription>Guests will no longer see it.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Guest list */}
        <div className="mt-6 card-soft" data-testid="guest-list">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Guest list</h2>
          {Object.keys(groupedRsvps).every((k) => groupedRsvps[k].length === 0) ? (
            <p className="text-sm text-stone-500">No responses yet. Be the first!</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedRsvps).map(([status, list]) =>
                list.length ? (
                  <div key={status}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-medium border rounded-full px-2 py-0.5 ${RSVP_META[status].pill}`}>
                        {RSVP_META[status].label} · {list.length}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {list.map((r) => (
                        <div key={r.user_id} className="flex items-center gap-2 bg-stone-50 rounded-full pl-1 pr-3 py-1" data-testid={`rsvp-user-${r.user_id}`}>
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={r.user_avatar} />
                            <AvatarFallback className="bg-teal-100 text-teal-700 text-[10px]">{getInitials(r.user_name)}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm text-stone-700">{r.user_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              )}
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="mt-6 card-soft" data-testid="event-comments">
          <h2 className="text-lg font-semibold text-stone-900 mb-3">Discussion</h2>
          <form onSubmit={submitComment} className="flex items-center gap-2 mb-4">
            <Input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Ask a question, bring something to the potluck…"
              className="rounded-full border-stone-200"
              data-testid="event-comment-input"
              maxLength={500}
            />
            <Button
              type="submit"
              disabled={!newComment.trim() || postingComment}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="event-comment-submit"
            >
              {postingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
          {comments.length === 0 ? (
            <p className="text-sm text-stone-500">No messages yet.</p>
          ) : (
            <div className="space-y-3">
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-3" data-testid={`event-comment-${c.id}`}>
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={c.user_avatar} />
                    <AvatarFallback className="bg-teal-100 text-teal-700 text-xs">{getInitials(c.user_name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium text-stone-900">{c.user_name}</span>
                      <span className="ml-2 text-xs text-stone-400">{format(parseISO(c.created_at), 'MMM d, h:mm a')}</span>
                    </p>
                    <p className="text-sm text-stone-700 mt-0.5 whitespace-pre-wrap">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <MobileNav />
    </div>
  );
}
